// Category helpers for Watchlist attribute columns.
//
// The backend treats values that differ only in case or spacing as the same
// category (normalize_attribute_value: collapse whitespace, NFC, casefold) and
// allows at most 50 categories per column. These helpers mirror that so the
// grid can count, filter, colour and replace categories the same way.

export { MAX_ATTRIBUTE_CATEGORIES } from '../../../utils/watchlistAttributeLimits';

/** Display form of a value: whitespace collapsed and trimmed. */
export function categoryDisplay(value) {
    return String(value ?? '').split(/\s+/).filter(Boolean).join(' ');
}

/** Comparison key of a value; '' means blank (Unassigned). */
export function categoryKey(value) {
    // JavaScript has no casefold; lower case plus the one common multi-letter
    // fold (German sharp s) matches Python's casefold for realistic labels.
    const key = categoryDisplay(value).normalize('NFC').toLowerCase().replace(/ß/g, 'ss');
    // The backend saves the legacy placeholder "Unknown" as Unassigned.
    return key === 'unknown' ? '' : key;
}

/**
 * Categories in a column with how many rows hold each, most common first.
 * Blank cells are reported separately so the filter can offer "(Blank)".
 * Each category shows its most frequent spelling.
 */
export function categoryFacets(values) {
    const byKey = new Map();
    let blankCount = 0;
    values.forEach((value) => {
        const key = categoryKey(value);
        if (!key) {
            blankCount += 1;
            return;
        }
        const display = categoryDisplay(value);
        const entry = byKey.get(key) || { key, count: 0, spellings: new Map() };
        entry.count += 1;
        entry.spellings.set(display, (entry.spellings.get(display) || 0) + 1);
        byKey.set(key, entry);
    });
    const categories = [...byKey.values()].map((entry) => {
        const [display] = [...entry.spellings.entries()]
            .sort((left, right) => right[1] - left[1] || left[0].localeCompare(right[0]))[0];
        return { key: entry.key, value: display, count: entry.count };
    }).sort((left, right) => right.count - left.count || left.value.localeCompare(right.value));
    return { categories, blankCount };
}

/** Number of distinct non-blank categories, as the backend counts them. */
export function countCategories(values) {
    const keys = new Set();
    values.forEach((value) => {
        const key = categoryKey(value);
        if (key) keys.add(key);
    });
    return keys.size;
}

/**
 * Cell writes that replace one category with another on the given rows of a
 * column (the caller decides which rows; rename passes every Watchlist row).
 * Rows that are not editable or hold another category are untouched.
 */
export function planReplaceCategory({ rowIds, columnId, readValue, isEditable, fromValue, toValue }) {
    const fromKey = categoryKey(fromValue);
    const replacement = categoryDisplay(toValue);
    return rowIds
        .filter((rowId) => isEditable(rowId, columnId)
            && categoryKey(readValue(rowId, columnId)) === fromKey)
        .map((rowId) => ({ rowId, columnId, value: replacement }));
}

/**
 * A stable palette slot for a category, so "AI" is the same colour in every
 * row, column and session. FNV-1a over the category key.
 */
export function categoryColorIndex(value, paletteSize) {
    const key = categoryKey(value);
    if (!key || paletteSize <= 0) return -1;
    let hash = 0x811c9dc5;
    for (let index = 0; index < key.length; index += 1) {
        hash ^= key.charCodeAt(index);
        hash = Math.imul(hash, 0x01000193) >>> 0;
    }
    return hash % paletteSize;
}
